if (!customElements.get("purchase-summary")) {
  customElements.define("purchase-summary", class extends HTMLElement {
    connectedCallback() {
      this.main = this.closest(".product-section");
      this.form = this.closest("form") || this.main?.querySelector(".product-single__form");
      this.variants = this.getVariants();
      this.currency = this.dataset.currency || "USD";
      this.accessorySection = this.querySelector("[data-purchase-accessories]");
      this.accessoryList = this.querySelector("[data-purchase-accessory-list]");
      this.quantityInput = this.form?.querySelector('input[name="quantity"]');
      this.buyNowButton = this.querySelector("[data-purchase-buy-now]");
      this.scheduleUpdate = () => {
        window.clearTimeout(this.updateTimer);
        this.updateTimer = window.setTimeout(() => this.update(), 0);
      };

      this.main?.addEventListener("change", this.scheduleUpdate);
      this.main?.addEventListener("input", this.scheduleUpdate);
      this.main?.addEventListener("click", this.scheduleUpdate);
      document.addEventListener("quantity:change", this.scheduleUpdate);
      this.buyNowButton?.addEventListener("click", () => this.buyNow());

      this.connectUpsellObserver();
      this.update();
    }

    disconnectedCallback() {
      this.upsellObserver?.disconnect();
      this.waitingObserver?.disconnect();
      window.clearTimeout(this.updateTimer);
      this.main?.removeEventListener("change", this.scheduleUpdate);
      this.main?.removeEventListener("input", this.scheduleUpdate);
      this.main?.removeEventListener("click", this.scheduleUpdate);
      document.removeEventListener("quantity:change", this.scheduleUpdate);
      this.upsellRoot?.removeEventListener("change", this.scheduleUpdate);
      this.upsellRoot?.removeEventListener("input", this.scheduleUpdate);
      this.upsellRoot?.removeEventListener("click", this.scheduleUpdate);
    }

    getVariants() {
      const data = this.main?.querySelector("[data-variant-json]")?.textContent;
      if (!data) return [];
      try {
        return JSON.parse(data);
      } catch (error) {
        return [];
      }
    }

    getSelectedOptions() {
      return [...(this.main?.querySelectorAll(".variant-input-wrap") || [])]
        .map((group) => group.querySelector('input[type="radio"]:checked')?.value)
        .filter(Boolean);
    }

    getCurrentVariant() {
      const options = this.getSelectedOptions();
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === options[index])
      ) || this.variants.find((variant) => String(variant.id) === this.form?.querySelector("[data-product-select]")?.value);
    }

    getProductQuantity() {
      return Math.max(1, Number(this.quantityInput?.value) || 1);
    }

    getSelectedAccessories() {
      return [...document.querySelectorAll(".app-bundle-list lb-upsell-flat-card")]
        .filter((card) => card.querySelector('input[type="checkbox"]')?.checked)
        .map((card) => {
          const variantSelect = card.querySelector("select.lb-variant-picker");
          const quantity = Math.max(1, Number(card.querySelector("input.lb-qty-count")?.value) || 1);
          const salePrice = card.querySelector(".lb-price-sale-price");
          const comparePrice = card.querySelector(".lb-price-compare-price");
          return {
            id: variantSelect?.value || card.getAttribute("lb-variant-id"),
            title: card.querySelector(".lb-card-title")?.textContent?.trim() || "Accessory",
            quantity,
            price: Number(salePrice?.getAttribute("lb-default-price")) || 0,
            comparePrice: Number(comparePrice?.getAttribute("lb-default-price")) || 0
          };
        })
        .filter((item) => item.id);
    }

    connectUpsellObserver() {
      const connect = () => {
        const root = document.querySelector(".app-bundle-list");
        if (!root || this.upsellObserver) return false;
        this.upsellRoot = root;
        root.addEventListener("change", this.scheduleUpdate);
        root.addEventListener("input", this.scheduleUpdate);
        root.addEventListener("click", this.scheduleUpdate);
        this.upsellObserver = new MutationObserver(this.scheduleUpdate);
        this.upsellObserver.observe(root, { childList: true, subtree: true, attributes: true });
        this.scheduleUpdate();
        return true;
      };

      if (!connect()) {
        this.waitingObserver = new MutationObserver(() => {
          if (connect()) this.waitingObserver.disconnect();
        });
        this.waitingObserver.observe(this.main || document.body, { childList: true, subtree: true });
      }
    }

    friendlyOption(value) {
      const normalized = value.toLowerCase();
      if (normalized.includes("without seat") || normalized.includes("no seat")) return "No seat kit included";
      if (normalized.includes("with seat")) return "Seat kit";
      return value;
    }

    updateOptions() {
      const choice = this.main?.querySelector('.variant-wrapper--choice input[type="radio"]:checked')?.value;
      const visual = this.main?.querySelector('.variant-wrapper--visual input[type="radio"]:checked')?.value;
      const values = [choice, visual].filter(Boolean).map((value) => this.friendlyOption(value));
      const target = this.querySelector("[data-purchase-options]");
      if (target) target.textContent = values.join("  ||  ");
    }

    updateAccessories(accessories) {
      this.accessoryList.replaceChildren(...accessories.map((accessory) => {
        const row = document.createElement("div");
        row.className = "purchase-summary-card__line";
        const name = document.createElement("span");
        name.textContent = accessory.title;
        const quantity = document.createElement("span");
        quantity.textContent = `x${accessory.quantity}`;
        row.append(name, quantity);
        return row;
      }));
      this.accessorySection.classList.toggle("is-empty", accessories.length === 0);
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: this.currency,
        minimumFractionDigits: 2
      }).format(cents / 100);
    }

    update() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const productQuantity = this.getProductQuantity();
      const accessories = this.getSelectedAccessories();
      const accessoryTotal = accessories.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const accessoryCompare = accessories.reduce((sum, item) => sum + (item.comparePrice || item.price) * item.quantity, 0);
      const total = variant.price * productQuantity + accessoryTotal;
      const compare = (variant.compare_at_price || variant.price) * productQuantity + accessoryCompare;
      const items = [
        { id: variant.id, quantity: productQuantity },
        ...accessories.map(({ id, quantity }) => ({ id, quantity }))
      ];

      if (!this.hasAttribute("data-summary-only")) {
        this.updateOptions();
        this.updateAccessories(accessories);
        this.querySelector("[data-purchase-product-quantity]").textContent = productQuantity;
        this.querySelector("[data-purchase-total]").textContent = this.formatMoney(total);

        const compareTarget = this.querySelector("[data-purchase-compare]");
        compareTarget.hidden = compare <= total;
        compareTarget.textContent = compare > total ? this.formatMoney(compare) : "";

        const image = this.querySelector("[data-purchase-image]");
        if (image && variant.featured_image?.src) image.src = variant.featured_image.src;

        this.buyNowButton.disabled = !variant.available;
        this.buyNowButton.dataset.variantId = variant.id;
      }

      const summary = {
        id: variant.id,
        available: variant.available,
        quantity: productQuantity,
        total,
        compare,
        totalFormatted: this.formatMoney(total),
        compareFormatted: compare > total ? this.formatMoney(compare) : "",
        items
      };
      window.productPurchaseSummary = summary;
      document.dispatchEvent(new CustomEvent("purchase-summary:change", { detail: summary }));
    }

    buyNow() {
      const variant = this.getCurrentVariant();
      if (!variant?.available) return;

      const items = [{ id: variant.id, quantity: this.getProductQuantity() }, ...this.getSelectedAccessories()];
      const cartItems = items.map((item) => `${item.id}:${item.quantity}`).join(",");
      const cartPath = window.theme?.routes?.cartPage || "/cart";
      window.location.assign(`${cartPath}/${cartItems}?checkout`);
    }
  });
}
